import { useEffect } from "react";
import { useLocation } from "wouter";
import posthog from "posthog-js";
import { usePostHog } from "posthog-js/react";

const DEFAULT_HOST = "https://us.i.posthog.com";

export function isPostHogEnabled(): boolean {
  return Boolean(import.meta.env.PROD && import.meta.env.VITE_POSTHOG_KEY);
}

export function initPostHog(): void {
  if (!isPostHogEnabled()) return;

  posthog.init(import.meta.env.VITE_POSTHOG_KEY as string, {
    api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? DEFAULT_HOST,
    capture_pageview: false, // fired manually on Wouter location change
    capture_pageleave: true,
    autocapture: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*", // PII-heavy app: mask all rendered text by default
    },
    persistence: "localStorage+cookie",
  });
}

export function getPostHogClient(): typeof posthog | null {
  return isPostHogEnabled() ? posthog : null;
}

/**
 * Fires a `$pageview` event on every Wouter location change.
 * PostHog's automatic SPA pageview detection does not recognize Wouter,
 * so we disable it in `initPostHog` and emit manually here.
 */
export function usePostHogPageview(): void {
  const [location] = useLocation();
  const ph = usePostHog();
  const enabled = isPostHogEnabled();

  useEffect(() => {
    if (!enabled || !ph) return;
    ph.capture("$pageview", { $current_url: window.location.href });
  }, [location, ph, enabled]);
}
